import { PageWrapper } from '../../components/PageWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PHONE_PASSWORD_LOGIN, HOME } from '../../constants/routes';
import { changePassword } from '../../actions/userPhoneAction';
import { submitPasswordForLogin } from '../../utils/phoneNumber';
import useAxios from '../../axios/useAxios';
import { notifySuccess } from '../../utils/Notify';
import { auth } from '../../firebase/firebaseConfig';
import { posthogPhoneNumberEvent } from '../../posthog/phoneNumberEvent';
import { POSTHOG_CAPTURE_PHONE_SIGN_UP_SUCCESS } from '../../posthog/constants';
import OtpInput from 'react18-input-otp';
import { BeatLoader } from 'react-spinners';
import PrimaryButton from '../../components/button/PrimaryButton';
import Lottie from 'lottie-react';
import eyeAnim from '../../styles/lottie/eye.json';
import eyeOffAnim from '../../styles/lottie/eye-off.json';

export const PhonePasswordCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxios();

  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);

  const { phoneNumber, isForgotPassword, isVerified, isNewUser } =
    location.state || {};

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [peekIndex, setPeekIndex] = useState(-1);
  const peekTimer = useRef(null);

  const buildInputProps = (peekIdx) =>
    Array.from({ length: 6 }, (_, i) => ({
      inputMode: 'numeric',
      pattern: '[0-9]*',
      type: showPassword || i === peekIdx ? 'text' : 'password',
    }));

  useEffect(() => {
    if (!phoneNumber || !isVerified) {
      navigate(PHONE_PASSWORD_LOGIN);
    }
  }, [phoneNumber, isVerified, navigate]);


  const handleSubmit = async () => {
    if (password.length !== 6) return;

    setLoading(true);
    setError('');

    try {
      if (isNewUser === true) {
        const payload = {
          user: {
            phoneNumber,
            password,
            firebaseUid: auth?.currentUser?.uid,
          },
          order: null,
          lastLoginAt: auth?.currentUser?.metadata?.lastLoginAt,
          isPWA: false,
        };

        const response = await axiosInstance.post(
          'user/phoneNumberSignUp',
          payload,
        );

        if (response.status === 201) {
          posthogPhoneNumberEvent(POSTHOG_CAPTURE_PHONE_SIGN_UP_SUCCESS, {
            phoneNumber,
          });
          // Login immediately after signup
          await submitPasswordForLogin({
            password,
            confirmPassword: password,
            isSignup: false,
            phoneNumber,
            setLoading,
            setPassword,
            auth,
            dispatch,
            axiosInstance,
            cartItems,
            navigate,
            userResult: auth.currentUser,
            setLockUntil: () => {},
            setError,
          });
        } else {
          setError(response?.data?.message || 'Création de compte échouée');
          setLoading(false);
        }
      } else {
        // Forgot password flow: change password then login
        const result = await changePassword(
          axiosInstance,
          phoneNumber,
          password,
          () => {},
        );

        if (result.success) {
          await submitPasswordForLogin({
            password,
            confirmPassword: password,
            isSignup: false,
            phoneNumber,
            setLoading,
            setPassword,
            auth,
            dispatch,
            axiosInstance,
            cartItems,
            navigate,
            userResult: auth.currentUser,
            setLockUntil: () => {},
            setError,
          });
        } else {
          setError(
            result?.serverError ||
              'Erreur lors de la modification du mot de passe',
          );
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Password change error:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la création du compte';
      setError(message);
      setLoading(false);
    }
  };

  const title = isForgotPassword
    ? '🔑 Créer un nouveau mot de passe'
    : '🔑 Crée ton mot de passe';

  return (
    <PageWrapper
      title={title}
      onGoBack={() =>
        isForgotPassword
          ? navigate(PHONE_PASSWORD_LOGIN, { state: { phoneNumber, userExists: true } })
          : navigate(HOME)
      }
    >
      <div className="flex flex-col items-center justify-center gap-6">
        {loading ? (
          <BeatLoader color="#183153" size={20} />
        ) : (
          <>
            {/* Password */}
            <div className="w-full">
              <p className="text-gray-500 text-lg mb-2 text-center">
                Entre 6 chiffres pour ton mot de passe
              </p>
              <div className="flex justify-center">
                <OtpInput
                  value={password}
                  onChange={(val) => {
                    setPassword(val);
                    setError('');
                    if (val.length > password.length) {
                      const newIndex = val.length - 1;
                      setPeekIndex(newIndex);
                      clearTimeout(peekTimer.current);
                      peekTimer.current = setTimeout(() => setPeekIndex(-1), 500);
                    }
                  }}
                  numInputs={6}
                  isInputNum={true}
                  isDisabled={loading}
                  shouldAutoFocus={true}
                  inputStyle="password-input"
                  isInputSecure={false}
                  inputProps={buildInputProps(peekIndex)}
                  separator={<span className="otp-separator" />}
                  containerStyle="otp-container"
                />
              </div>
            </div>

            {/* Toggle visibility */}
            <div
              className="cursor-pointer flex items-center gap-2"
              onClick={() => setShowPassword(!showPassword)}
            >
              <Lottie
                animationData={showPassword ? eyeOffAnim : eyeAnim}
                loop={false}
                autoplay={true}
                style={{ width: '20px', height: '20px' }}
              />
              <span className="text-gray-500 text-sm">
                {showPassword ? 'Masquer' : 'Afficher'}
              </span>
            </div>

            {/* Submit button */}
            <PrimaryButton
              label="Valider"
              onClick={handleSubmit}
              disabled={password.length < 6}
              isLoading={loading}
              className="mt-4 w-full"
            />
          </>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full">
            <p className="text-red-600 text-lg font-semibold text-center">
              {error}
            </p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <p className="text-gray-500 text-lg mt-2">Modification en cours...</p>
        )}
      </div>
    </PageWrapper>
  );
};
